/* eslint-disable operator-linebreak */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { parseISO } from 'date-fns';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import some from 'lodash/some';
import values from 'lodash/values';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-media';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Grid, Header, Icon, Message, Modal, Table } from 'semantic-ui-react';
import EntityLink from '../../../components/EntityLink';
import { bem } from '../../../core/design/bem';
import { workshopArrayProptypes, workshopProptypes } from '../../../propTypes';
import { generateWorkshopStatus } from '../../../utils/groupUtils';
import { GLOBAL_MEDIA_QUERIES } from '../../../utils/mediaQueries';
import { sweetAlert } from '../../../utils/popupUtils';
import WorkshopLineFields, { findOverlappingSession } from './WorkshopLineFields';
import WorkshopMultipleValidation from './WorkshopMultipleValidation';
import './WorkshopTable.scss';

const css = bem('WorkshopTable');
const translationPrefix = 'workshops.workshop';

export function formatDate(date, timezone, format) {
  return moment(parseISO(date)).tz(timezone).format(format);
}

export function showErrors(errors) {
  sweetAlert({
    icon: 'warning',
    width: '60%',
    html: <Message warning header="Attention !" list={errors?.map((e) => e.message)} />,
  });
}

export function formatWorkshops(workshops, timezone) {
  const groupByFormat = (date) => formatDate(date, timezone, 'HH:mm');
  const formattedWorkshops = workshops.map((item) => ({
    ...item,
    startDateTz: moment(parseISO(item?.startDate)).tz(timezone),
    endDateTz: moment(parseISO(item?.endDate)).tz(timezone),
    groupByTime: `${groupByFormat(item?.startDate)} - ${groupByFormat(item?.endDate)}`,
    day: formatDate(item?.startDate, timezone, 'YYYY-MM-DD'),
  }));

  const days = Object.keys(
    groupBy(workshops, (item) => {
      const value = get(item, 'startDate');
      if (!value) return '';
      return formatDate(item?.startDate, timezone, 'YYYY-MM-DD');
    }),
  );
  return { days, formattedWorkshops };
}

/**
 * If we have a mandatory workshop, hide the other ones
 * @param {*} workshops
 */
function hideNonRegistrableWorkshops(workshops) {
  const hasMandatory = some(workshops, (w) => w.mandatory);
  if (hasMandatory) {
    // Keep only mandatory workshops
    return workshops.filter((w) => w.mandatory);
  }
  return workshops;
}

function numberOrUndefined(value) {
  if (typeof value === 'number') return value;
  if (Number.isNaN(value)) return undefined;
  return parseInt(value, 10);
}

function generateDateGroups(workshops) {
  const groups = groupBy(
    orderBy(
      workshops,
      ['startDate', 'endDate', (w) => numberOrUndefined(w.order)],
      ['asc', 'desc', 'asc'],
    ),
    (item) => {
      const value = get(item, 'groupByTime');
      if (!value) return '';
      return value;
    },
  );

  const times = Object.keys(groups);
  return times.map((key) => ({
    id: key,
    label: key,
    items: groups[key],
    itemsByDay: groupBy(groups[key], 'day'),
  }));
}

export function useRequiredTimeSlots(days, workshopsByDay) {
  return useMemo(() => {
    const required = {};
    days.forEach((day) => {
      const dayTimes = generateDateGroups(get(workshopsByDay, day, []));
      dayTimes.forEach(({ items }) => {
        const visibleItems = hideNonRegistrableWorkshops(items);
        if (some(visibleItems, (w) => w.choiceRequired)) {
          required[visibleItems[0].startDate] = true;
        }
      });
    });
    return required;
  }, [days, workshopsByDay]);
}

const registrationFields = ['title', 'collection', 'startDate', 'endDate'];

const Actions = ({ workshop }) => {
  const { replays = [] } = workshop;
  const { t } = useTranslation();
  return (
    <div className="actions">
      <Button size="tiny" className="watch-later">
        {t(`${translationPrefix}.watch-later`)}
      </Button>
      {replays.length > 0 && (
        <Button size="tiny" className="replay">
          {t(`${translationPrefix}.replay`)}
        </Button>
      )}
    </div>
  );
};

Actions.propTypes = {
  workshop: PropTypes.shape(workshopProptypes).isRequired,
};

function removeImpossibleSessions(sessions) {
  const mandatorySessions = sessions.filter((s) => s.mandatory);
  return sessions.filter((session) => {
    return !findOverlappingSession(session, mandatorySessions);
  });
}

function WorkshopTable(props) {
  const { t } = useTranslation();
  const [selectedSessions, setSelectedSessions] = useState({});
  const [selectedDay, setSelectedDay] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const { desktop } = useMedia({ queries: GLOBAL_MEDIA_QUERIES });
  const registrations = useSelector((state) => state.registrations.registrations);
  const registrationsByDate = mapValues(keyBy(registrations, 'startDate'), (r) => {
    return { ...pick(r, registrationFields), workshopId: r.workshopId, sessionId: r._id };
  });

  const {
    filteredWorkshopIds,
    workshopList,
    timezone,
    fields: tableFields,
    actions,
    splitDays,
    ...rest
  } = props;
  const { registerOncePerSlot, validationConfig } = rest;
  const workshops = generateWorkshopStatus(workshopList);
  const { days, formattedWorkshops } = formatWorkshops(workshops, timezone);

  const workshopsByDay = useMemo(() => {
    const byDay = groupBy(formattedWorkshops, 'day');
    return mapValues(byDay, removeImpossibleSessions);
  }, [formattedWorkshops]);
  const requiredChoiceTimeSlots = useRequiredTimeSlots(days, workshopsByDay);

  if (!workshops.length) return null;

  const handleSelect = (session) => {
    const { startDate, _id, workshopId } = session;
    const isSession = !!workshopId;
    const sessionId = workshopId || _id;
    const newSelectedSessions =
      startDate in selectedSessions
        ? omit(selectedSessions, startDate)
        : {
            ...selectedSessions,
            [startDate]: {
              ...pick(session, registrationFields),
              workshopId: isSession ? workshopId : sessionId,
              sessionId: isSession ? _id : undefined,
            },
          };
    setSelectedSessions(newSelectedSessions);
  };

  const fields = desktop ? tableFields : orderBy(tableFields, (f) => f?.mobile?.order, 'asc');

  // Combine registration and current selection, and omit empty registrations
  const combinedSelectedSessions = omitBy(
    {
      ...registrationsByDate,
      ...selectedSessions,
    },
    (v) => !v,
  );

  const workshopLineProps = {
    selectedSessions: combinedSelectedSessions,
    registerOncePerSlot,
    actions,
    fields,
  };

  const requiredTimeSlots = keys(requiredChoiceTimeSlots);
  const requiredSelectedCount = values(pick(combinedSelectedSessions, requiredTimeSlots)).length;
  const hasRequiredSlots = requiredSelectedCount >= requiredTimeSlots.length;

  if (splitDays) {
    return (
      <>
        {days.map((day) => {
          const dayTimes = generateDateGroups(get(workshopsByDay, day, []));

          return (
            <Grid key={day} stackable className={css().toString()}>
              <Header className="day">{t('program.table.day', { day })}</Header>
              <Table stackable structured>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell className="time" />
                    {fields.map((field) => (
                      <Table.HeaderCell key={field.key} className={field.key}>
                        {field.label}
                      </Table.HeaderCell>
                    ))}
                    <Table.HeaderCell className="actions" />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {dayTimes.map(({ id, items }) => {
                    const visibleItems = hideNonRegistrableWorkshops(items);
                    const choiceRequired = some(visibleItems, (w) => w.choiceRequired);
                    return (
                      <React.Fragment key={id}>
                        {visibleItems.map((session, index) => (
                          <Table.Row
                            key={session._id}
                            className={index === 0 ? 'new-slot' : undefined}
                          >
                            {index === 0 && (
                              <Table.Cell rowSpan={visibleItems.length} className="time">
                                {t('program.table.time', {
                                  startDate: session?.startDate,
                                  endDate: session?.endDate,
                                })}
                                {choiceRequired && (
                                  <div className="choice-required">
                                    {t('program.table.choice-required')}
                                  </div>
                                )}
                              </Table.Cell>
                            )}
                            <WorkshopLineFields
                              {...workshopLineProps}
                              session={session}
                              onSelect={handleSelect}
                            />
                          </Table.Row>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </Table.Body>
              </Table>
            </Grid>
          );
        })}
        {((Object.keys(selectedSessions).length > 0 && registerOncePerSlot) ||
          !hasRequiredSlots) && (
          <WorkshopMultipleValidation
            config={validationConfig}
            selectedSessions={selectedSessions}
            combinedSelectedSessions={combinedSelectedSessions}
            requiredChoiceTimeSlots={requiredChoiceTimeSlots}
            onSelect={() => setSelectedSessions({})}
          />
        )}
      </>
    );
  }

  const times = generateDateGroups(
    desktop
      ? formattedWorkshops
      : formattedWorkshops.filter((w) => w.day === (selectedDay || days?.[0])),
  );

  return (
    <Grid stackable className={css().toString()}>
      {!desktop && (
        <Dropdown
          icon="chevron down"
          className={css('days').toString()}
          value={selectedDay || days?.[0]}
          fluid
          selection
          onChange={(_e, { value }) => setSelectedDay(value)}
          options={days.map((day) => ({
            key: day,
            text: t('program.table.day', { day }),
            value: day,
          }))}
        />
      )}
      <Table unstackable striped>
        {desktop && (
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="time" style={{ minWidth: 130, visibility: 'hidden' }} />
              {days.map((day) => (
                <Table.Cell key={day}>{t('program.table.day', { day })}</Table.Cell>
              ))}
            </Table.Row>
          </Table.Header>
        )}
        <Table.Body>
          {times.map(({ id, items, itemsByDay }) => {
            const session = items[0];
            return (
              <Table.Row key={id}>
                <Table.Cell className="time">
                  {t('program.table.time', {
                    startDate: session?.startDateTz,
                    endDate: session?.endDateTz,
                  })}
                </Table.Cell>
                {days.map((day) => {
                  const dayItems = get(itemsByDay, day, []);
                  return (
                    <Table.Cell
                      className={css('workshops').toString()}
                      style={{ cursor: 'pointer', padding: 0 }}
                      key={day}
                    >
                      {dayItems.map((workshop) => {
                        // eslint-disable-next-line no-shadow
                        const { _id, workshopId, title } = workshop;
                        const sessionId = workshopId || _id;
                        const isHighlighted = filteredWorkshopIds?.includes(sessionId) || false;
                        return (
                          <div
                            key={sessionId}
                            className={css('workshop', { workshopId: sessionId })
                              .state({ highlighted: isHighlighted })
                              .toString()}
                          >
                            <EntityLink
                              className="title"
                              entity={{ ...workshop, kind: 'workshops' }}
                            >
                              {title}
                            </EntityLink>
                            {desktop && <Actions workshop={workshop} />}
                            {!desktop && (
                              <>
                                <div
                                  style={{ width: 30 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsOpen(sessionId);
                                  }}
                                >
                                  <Icon className="ellipsis" name="ellipsis vertical" />
                                </div>
                                {isOpen === sessionId && (
                                  <Modal
                                    className={css('options').toString()}
                                    open
                                    closeIcon
                                    onClose={() => setIsOpen(false)}
                                  >
                                    <Modal.Header>Options</Modal.Header>
                                    <Modal.Content>
                                      <Actions workshop={workshop} />
                                    </Modal.Content>
                                  </Modal>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </Grid>
  );
}

WorkshopTable.defaultProps = {
  actions: [],
  fields: [{ key: 'title' }],
  filteredWorkshopIds: undefined,
  groupBy: undefined,
  splitDays: true,
  timezone: 'Europe/London',
  workshopList: [],
};

WorkshopTable.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(PropTypes.object),
  filteredWorkshopIds: PropTypes.arrayOf(PropTypes.string),
  groupBy: PropTypes.shape({
    field: PropTypes.string.isRequired,
    type: PropTypes.string,
  }),
  splitDays: PropTypes.bool,
  timezone: PropTypes.string,
  workshopList: workshopArrayProptypes,
};

export default WorkshopTable;
